"use client";

import { useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Play, X } from "lucide-react";
function VideoGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [selectedVideo, setSelectedVideo] = useState<{
    url: string;
    title: string;
  } | null>(null);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-64 rounded-lg bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground">Failed to load videos. Please try again later.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground">No videos available at the moment.</p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const videoUrl = post.customFields?.url || "";
        const thumbnailUrl = post.customFields?.thumbnail || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1524742/img/video_placeholder.png";
        const videoTitle = post.customFields?.title || post.title || "Untitled Video";
        return <Card key={post.id} data-index={i} className="group cursor-pointer overflow-hidden transition-all duration-[80ms] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-medium" onClick={() => setSelectedVideo({
          url: videoUrl,
          title: videoTitle
        })}>
              <CardContent className="relative p-0">
                <div className="relative aspect-video overflow-hidden">
                  <img src={thumbnailUrl} alt={videoTitle} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity duration-[80ms] group-hover:bg-black/50">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/90 transition-transform duration-[80ms] group-hover:scale-110 group-hover:animate-pulse">
                      <Play className="h-8 w-8 fill-foreground-accent text-foreground-accent" />
                    </div>
                  </div>
                </div>
                {videoTitle && <div className="p-4">
                    <h3 className="text-base font-semibold text-foreground line-clamp-2">
                      {videoTitle}
                    </h3>
                  </div>}
              </CardContent>
            </Card>;
      })}
      </div>

      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl bg-background/95 backdrop-blur-md p-0 overflow-hidden">
          <DialogTitle className="sr-only">{selectedVideo?.title || "Video Player"}</DialogTitle>
          <div className="relative aspect-video w-full">
            {selectedVideo?.url && <iframe src={selectedVideo.url} title={selectedVideo.title} className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />}
          </div>
          <button onClick={() => setSelectedVideo(null)} className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm transition-all duration-[80ms] hover:scale-110 hover:bg-background focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Close video">
            <X className="h-5 w-5 text-foreground" />
          </button>
        </DialogContent>
      </Dialog>
    </>;
}
export default function VideoGallery() {
  return <section id="video-gallery" className="bg-background py-16 px-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-display font-bold text-foreground mb-4">
            Video Gallery
          </h2>
          <p className="text-muted-foreground text-lg">
            Explore our collection of videos
          </p>
        </div>

        <PostsProvider postType="video" wp_query={{
        per_page: 12,
        order: "desc",
        orderby: "date"
      }} embeds={[]} fields={["id", "title.rendered", "meta"]}>
          <VideoGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}